import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Quote, X, Download, CreditCard, Copy, Check, Smartphone, Loader2, Phone } from 'lucide-react';

const SD_CARD_URL = `${process.env.REACT_APP_BACKEND_URL}/api/misc/sd-card-cutout.png`;
const API = process.env.REACT_APP_BACKEND_URL;
const ZELLE_EMAIL = 'thevaadotzar@gmail.com';
const AKIVA_PHONE = '305-497-9507';
const AKIVA_TEL = 'sms:+13054979507';
const SD_QUANTITIES = [1, 5, 10, 25];

const TESTIMONIALS = [
  { quote: "I met my grandfather R' Tropper through listening to him on The Vaad Otzar — he died when I was 7." },
  { quote: "I'll never be bored thanks to The Vaad Otzar — I've already listened to 300 vaadim!" },
  { quote: "I've been running around telling people to stop what they're doing and listen." },
  { quote: "I just listened to 10 hours of Hashkafa in the past 3 days." },
  { quote: "I listened for 26 hours during my bein hazmanim, thanks!" },
  { quote: "This is the CC Hall of Fame!" },
  { quote: "You ruined R' Grunblatt's vaad for me — I already heard most of the stories on The Vaad Otzar!" },
  { quote: "I told my wife we're not listening to TorahAnytime shiurim anymore, just The Vaad Otzar Google Drive!" },
  { quote: "Maybe people will come to CC because of this?" },
  { quote: "Wow!!! I have what to listen to in the car for the next year! Tizku l'mitzvos — a real zikui harabbim, hatzalah with it!" },
  { quote: "I listened to 20 of R' Shafier's 45-minute Shmuz's already in the past week and a half!" },
  { quote: "Thanks so much — our whole car was all listening together for 4 hours." },
  { quote: "This is an entire room in Queens in a tiny thing — wow, look at the names, they go on forever!" },
  { quote: "We were branching and felt we could use chizuk. It was just the four of us Thursday night, and at 10:30 we realized we were missing the vaad — and someone said 'hey, The Otzar!' We just listened to one now. Thanks for being a lifesaver!" },
];

const HaskamaButton = () => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [qty, setQty] = useState(1);
  const [customAmt, setCustomAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  // Feb 2026 (user "make donate be the top option in more on mobile view
  // and be connected to all payment options in haskama"): auto-open the
  // modal whenever `?donate=1` is in the URL. The MorePage donate item
  // links to `/?donate=1` and the DonatePage forwards to the same query,
  // so every donate entry-point lands in this rich payment view.
  // Driven by the router location (not a one-shot mount check) so it also
  // survives the boot-time remount and client-side navigations to /?donate=1.
  const location = useLocation();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('donate') === '1') setOpen(true);
  }, [location.search]);

  const close = () => {
    setOpen(false);
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('donate') === '1') {
        params.delete('donate');
        const cleaned = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (cleaned ? `?${cleaned}` : '') + window.location.hash);
      }
    } catch { /* ignore */ }
  };

  // A blank/invalid `customAmt` falls back to the SD-card preset flow.
  const parsedCustom = parseInt(customAmt, 10);
  const isCustom = Number.isFinite(parsedCustom) && parsedCustom >= 5;
  const total = isCustom ? parsedCustom : qty * 10;

  const sponsor = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      const res = await fetch(`${API}/api/payments/donate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          custom_amount: total,
          origin_url: window.location.origin,
          donor_note: isCustom
            ? `Custom donation of $${total} to The Vaad Otzar`
            : `Sponsor ${qty} Vaad Otzar micro-SD card${qty === 1 ? '' : 's'}`,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const { checkout_url } = await res.json();
      window.location.href = checkout_url;
    } catch (e) {
      setErr(e.message || 'Could not start checkout');
      setBusy(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(ZELLE_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  // Hide the pill as soon as the user scrolls *any* inner scroll container
  // (each page has its own `.overflow-y-auto` div, so window scroll never
  //  fires — scroll events don't bubble either, so we attach listeners to
  //  every scroll container and re-attach when the DOM changes.)
  useEffect(() => {
    const attached = new WeakSet();
    const handleScroll = (e) => {
      const top = (e.target && e.target.scrollTop) || 0;
      setVisible(top < 40);
    };
    const attachAll = () => {
      document.querySelectorAll('.overflow-y-auto').forEach((el) => {
        if (attached.has(el)) return;
        attached.add(el);
        el.addEventListener('scroll', handleScroll, { passive: true });
      });
    };
    attachAll();
    const observer = new MutationObserver(attachAll);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          // Feb 2026 (user "hovering SD card isnt seeable"): the pill
          // was pinned at top-0.5 (2px from viewport top) which hides
          // it UNDER the iOS notch / OS status bar on mobile PWAs.
          // Anchor it to the safe-area inset instead so it always
          // clears the notch and stays fully tappable on every device.
          top: 'max(env(safe-area-inset-top, 0px), 6px)',
        }}
        className={`fixed right-1 sm:right-6 md:right-8 z-40 group inline-flex items-center gap-1 pl-1.5 pr-2 sm:pl-2 sm:pr-2.5 py-[2px] sm:py-1 rounded-full bg-gradient-to-b from-[#b9942d] to-[#8c691b] hover:from-[#c9a437] hover:to-[#9a7523] text-white text-[10px] sm:text-[11px] font-semibold shadow-[0_4px_12px_-4px_rgba(154,123,36,0.55)] hover:shadow-[0_6px_16px_-4px_rgba(154,123,36,0.75)] border border-[#e5c976]/40 backdrop-blur-sm transition-all duration-200 whitespace-nowrap leading-none ${
          visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-3 pointer-events-none'
        }`}
        data-testid="haskama-button"
        aria-label="Open testimonials and SD card sponsorship"
        aria-hidden={!visible}
        title="Testimonials"
      >
        <Quote className="w-3 h-3 text-[#fff2c6] group-hover:rotate-[-4deg] transition-transform" />
        <span className="whitespace-nowrap">
          {/* Feb 2026 (user "Haskama compact"): on mobile only the word
              "Haskama" fits without pushing the pill off-screen; on
              desktop show the full attribution. */}
          <span>Testimonials</span>
        </span>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-start justify-center pt-[4vh] pb-8 px-4"
          onMouseDown={close}
          data-testid="haskama-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="haskama-title"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm pointer-events-none" />
          <div
            onMouseDown={(e) => e.stopPropagation()}
            className="relative z-10 w-full max-w-3xl max-h-full flex flex-col rounded-2xl border border-[#e5c976]/25 bg-[#0f1729] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden"
            data-testid="haskama-modal"
          >
            <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3 border-b border-white/10 bg-gradient-to-r from-[#0f1729] to-[#182242]">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#e5c976]">
                  The Vaad Otzar
                </p>
                <h2
                  id="haskama-title"
                  className="text-white text-sm md:text-base font-bold leading-tight mt-0.5"
                >
                  Testimonials &amp; SD Card Sponsorship
                </h2>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* Feb 2026 (user "get rid of the download button"): removed. */}
                <button
                  type="button"
                  onClick={close}
                  className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Close"
                  data-testid="haskama-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto p-4 sm:p-5 bg-[#ffffff]">
              {/* Testimonials */}
              <section
                className="mb-2 rounded-xl bg-gradient-to-br from-[#f7f5f0] to-[#efe8d6] border border-[#e1ddd6] p-5 md:p-7"
                data-testid="haskama-testimonials"
              >
                <div className="text-center mb-5">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#9a7b24]">
                    In their own words
                  </p>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#0f1729] mt-1">
                    Testimonials
                  </h3>
                  <p className="text-[#4b5563] text-sm mt-1">
                    From bochurim, alumni, and families across the CC network
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {TESTIMONIALS.map((t, i) => (
                    <blockquote
                      key={i}
                      className="relative bg-white rounded-lg border border-[#e1ddd6] p-4 md:p-5 shadow-sm"
                      data-testid={`testimonial-${i}`}
                    >
                      <span
                        className="absolute -top-3 left-4 text-4xl leading-none font-serif text-[#9a7b24]/50 select-none"
                        aria-hidden="true"
                      >
                        “
                      </span>
                      <p className="text-[#0f1729] text-sm md:text-[15px] leading-relaxed pl-2">
                        {t.quote}
                      </p>
                    </blockquote>
                  ))}
                </div>
              </section>
              {/* SD-card sponsorship section — scroll below the letter.
                  Feb-2026: mobile & desktop now use the SAME dark-navy
                  gradient with gold accents (user asked for parity). The
                  earlier warm-cream mobile treatment has been dropped. */}
              <section
                className="mt-8 rounded-xl bg-gradient-to-br from-[#0f1729] via-[#132043] to-[#0f1729] text-white p-4 sm:p-5 md:p-7 relative overflow-hidden"
                data-testid="sd-sponsor-section"
              >
                {/* Feb 2026: dropped the two big warm-gold blurs behind the
                    SD card — the user wanted the card "cut out", not haloed. */}

                <div className="relative flex flex-col md:flex-row gap-6 items-center">
                  {/* SD card image — no background halo, larger, and no blur
                      filter (Feb 2026: user asked to strip the yellow box and
                      make the hover crisp). Kept the float animation. */}
                  <div
                    className="shrink-0 relative"
                    data-testid="sd-card-image-wrap"
                  >
                    <img
                      src={SD_CARD_URL}
                      alt="The Vaad Otzar 128 GB micro SD card"
                      className="relative w-64 sm:w-72 md:w-80 h-auto sd-card-float drop-shadow-[0_25px_45px_rgba(0,0,0,0.35)]"
                      data-testid="sd-card-image"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#e5c976] mb-1">
                      Sponsor a Card
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold leading-snug mb-3">
                      Help us reach the next 500 Bochurim
                    </h3>
                    <p className="text-white/85 text-sm leading-relaxed">
                      The Vaad Otzar has already given out <b>200 micro-SD cards</b> and is trying to give out <b>500 more 128&nbsp;GB micro-SD cards</b> to Bochurim and Yungerleit in Queens, Bochurim in the branches, and Alumni. Please consider sponsoring at just <b className="text-[#e5c976]">$10 a card</b>.
                    </p>
                    <p className="text-white/70 text-xs mt-3 leading-relaxed">
                      The micro-SD is perfect for the many bochurim on flip-phones and can be plugged straight into a car USB adapter for the drive to Yeshiva or work.
                    </p>
                  </div>
                </div>

                {/* Quantity picker + Stripe */}
                <div className="relative mt-6 rounded-lg bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-white/80 mb-2">
                    How many cards would you like to sponsor?
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {SD_QUANTITIES.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => { setQty(n); setCustomAmt(''); }}
                        className={`py-2 rounded-md text-sm font-bold transition-colors ${
                          qty === n && !isCustom
                            ? 'bg-[#e5c976] text-[#0f1729]'
                            : 'bg-white/5 text-white hover:bg-white/10'
                        }`}
                        data-testid={`sd-qty-${n}`}
                      >
                        {n} card{n === 1 ? '' : 's'}
                      </button>
                    ))}
                  </div>
                  {/* Custom donation amount — donors can give any dollar
                      amount without buying a card. Falls back to preset
                      quantity as soon as the field is emptied. */}
                  <div className="flex items-center gap-2 mb-3">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-white/70 shrink-0">
                      Or donate any amount
                    </label>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 text-sm font-semibold">$</span>
                      <input
                        type="number"
                        min="5"
                        step="1"
                        value={customAmt}
                        onChange={(e) => setCustomAmt(e.target.value)}
                        placeholder="Custom amount"
                        className="w-full pl-6 pr-3 py-1.5 rounded-md bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:border-white/40 placeholder:text-white/40"
                        data-testid="sd-custom-amount"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/70 text-sm">Total</span>
                    <span className="text-2xl font-bold text-[#e5c976]" data-testid="sd-total">${total}</span>
                  </div>
                  <button
                    type="button"
                    onClick={sponsor}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-[#e5c976] hover:bg-[#f0d989] disabled:opacity-50 text-[#0f1729] font-bold text-sm shadow-md transition-all"
                    data-testid="sd-sponsor-stripe"
                  >
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Starting Checkout…</>
                          : <><CreditCard className="w-4 h-4" />Sponsor with card via Stripe</>}
                  </button>
                  {err && <p className="mt-2 text-xs text-red-300" data-testid="sd-sponsor-error">{err}</p>}
                </div>

                {/* Zelle QR fallback */}
                <div className="relative mt-4 rounded-lg bg-white/[0.06] border border-white/10 p-4 flex items-center gap-4">
                  <div className="shrink-0 bg-white rounded-md p-1.5">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent('mailto:' + ZELLE_EMAIL)}`}
                      alt="Zelle QR code"
                      className="w-20 h-20 block"
                      data-testid="sd-zelle-qr"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Smartphone className="w-3.5 h-3.5 text-[#e5c976]" />
                      <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e5c976]">Zelle</p>
                    </div>
                    <p className="text-white text-sm font-semibold truncate">{ZELLE_EMAIL}</p>
                    <button
                      type="button"
                      onClick={copyEmail}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-white/80 hover:text-white bg-white/5 hover:bg-white/10 rounded-md px-2 py-1"
                      data-testid="sd-zelle-copy"
                    >
                      {copied ? <><Check className="w-3 h-3 text-green-300" />Copied</> : <><Copy className="w-3 h-3" />Copy email</>}
                    </button>
                  </div>
                </div>

                {/* Contact */}
                <div className="relative mt-4 text-center">
                  <p className="text-white/70 text-xs">Questions? Contact Akiva Paris</p>
                  <a
                    href={AKIVA_TEL}
                    className="inline-flex items-center gap-1.5 mt-1 text-[#e5c976] hover:text-[#f0d989] text-sm font-bold"
                    data-testid="sd-akiva-phone"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    {AKIVA_PHONE}
                  </a>
                </div>

                {/* Feb 2026 (user "SD card isnt seen hovering"): float
                    animation moved to the app-wide stylesheet so it
                    applies uniformly — inline modal <style> tags were
                    sometimes lost on mobile Safari when React re-rendered
                    the modal, killing the float. */}
              </section>

            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default HaskamaButton;
